import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Pencil, Save, X, Mail, Phone, MapPin, Linkedin, Github, Globe, DollarSign, Calendar, Briefcase, Loader2, Search } from 'lucide-react';
import { LushaDiscoverButton } from '@/components/lusha';

interface EditableContactInfoProps {
  candidate: any;
  onUpdate: () => void;
}

// Get the most recent company info from experience
const getMostRecentCompanyInfo = (candidate: any): { name: string; domain?: string } => {
  const experiences = candidate?.candidate_experience || [];
  if (experiences.length === 0) return { name: '' };
  
  // Prioritize current job, otherwise most recent by end_date or start_date
  const sorted = [...experiences].sort((a, b) => {
    if (a.is_current && !b.is_current) return -1;
    if (!a.is_current && b.is_current) return 1;
    const dateA = a.end_date || a.start_date || '';
    const dateB = b.end_date || b.start_date || '';
    return dateB.localeCompare(dateA);
  });
  
  const company = sorted[0]?.companies;
  const companyName = company?.name || sorted[0]?.company_name || '';
  
  // Try to extract domain from company website_url if available
  let domain: string | undefined;
  if (company?.website_url) {
    try {
      const url = new URL(company.website_url.startsWith('http') ? company.website_url : `https://${company.website_url}`);
      domain = url.hostname.replace('www.', '');
    } catch {
      // Invalid URL, ignore
    }
  }
  
  return { name: companyName, domain };
};

const EditableContactInfo = ({ candidate, onUpdate }: EditableContactInfoProps) => {
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    first_name: candidate?.first_name || '',
    last_name: candidate?.last_name || '',
    email_primary: candidate?.email_primary || '',
    phone_primary: candidate?.phone_primary || '',
    city: candidate?.city || '',
    address: candidate?.address || '',
    linkedin_url: candidate?.linkedin_url || '',
    github_url: candidate?.github_url || '',
    website_url: candidate?.website_url || '',
    desired_salary_min: candidate?.desired_salary_min || '',
    desired_salary_max: candidate?.desired_salary_max || '',
    desired_salary_currency: candidate?.desired_salary_currency || 'USD',
    availability_status: candidate?.availability_status || 'available',
    availability_date: candidate?.availability_date || '',
    employment_status: candidate?.employment_status || 'employed',
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('candidates')
        .update({
          ...formData,
          desired_salary_min: formData.desired_salary_min || null,
          desired_salary_max: formData.desired_salary_max || null,
          availability_date: formData.availability_date || null,
        })
        .eq('id', candidate.id);

      if (error) throw error;
      
      toast({ title: 'Contact info updated' });
      setEditing(false);
      onUpdate();
    } catch (error: any) {
      toast({ title: 'Error saving', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const formatSalary = (min: number | null, max: number | null, currency: string | null) => {
    if (!min && !max) return null;
    const curr = currency || 'USD';
    if (min && max) return `${curr} ${min.toLocaleString()} - ${max.toLocaleString()}`;
    if (min) return `${curr} ${min.toLocaleString()}+`;
    if (max) return `Up to ${curr} ${max.toLocaleString()}`;
    return null;
  };

  if (editing) {
    return (
      <Card className="p-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold">Edit Contact Info</h3>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={saving}>
              <X className="h-4 w-4" />
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              <span className="ml-1">Save</span>
            </Button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>First Name</Label>
            <Input 
              value={formData.first_name} 
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} 
            />
          </div>
          <div>
            <Label>Last Name</Label>
            <Input 
              value={formData.last_name} 
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} 
            />
          </div>
          <div>
            <Label>Email</Label>
            <Input 
              type="email"
              value={formData.email_primary} 
              onChange={(e) => setFormData({ ...formData, email_primary: e.target.value })} 
            />
          </div>
          <div>
            <Label>Phone</Label>
            <Input 
              value={formData.phone_primary} 
              onChange={(e) => setFormData({ ...formData, phone_primary: e.target.value })} 
            />
          </div>
          <div>
            <Label>City</Label>
            <Input 
              value={formData.city} 
              onChange={(e) => setFormData({ ...formData, city: e.target.value })} 
            />
          </div>
          <div>
            <Label>Address</Label>
            <Input 
              value={formData.address} 
              onChange={(e) => setFormData({ ...formData, address: e.target.value })} 
            />
          </div>
          <div>
            <Label>LinkedIn URL</Label>
            <Input 
              value={formData.linkedin_url} 
              onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })} 
            />
          </div>
          <div>
            <Label>GitHub URL</Label>
            <Input 
              value={formData.github_url} 
              onChange={(e) => setFormData({ ...formData, github_url: e.target.value })} 
            />
          </div>
          <div>
            <Label>Website URL</Label>
            <Input 
              value={formData.website_url} 
              onChange={(e) => setFormData({ ...formData, website_url: e.target.value })} 
            />
          </div>
          <div>
            <Label>Employment Status</Label>
            <Select 
              value={formData.employment_status} 
              onValueChange={(v) => setFormData({ ...formData, employment_status: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="employed">Employed</SelectItem>
                <SelectItem value="unemployed">Unemployed</SelectItem>
                <SelectItem value="freelancer">Freelancer</SelectItem>
                <SelectItem value="student">Student</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Availability Status</Label>
            <Select 
              value={formData.availability_status} 
              onValueChange={(v) => setFormData({ ...formData, availability_status: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="open_to_offers">Open to Offers</SelectItem>
                <SelectItem value="not_looking">Not Looking</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Availability Date</Label>
            <Input 
              type="date"
              value={formData.availability_date} 
              onChange={(e) => setFormData({ ...formData, availability_date: e.target.value })} 
            />
          </div>
          <div>
            <Label>Salary Currency</Label>
            <Select 
              value={formData.desired_salary_currency} 
              onValueChange={(v) => setFormData({ ...formData, desired_salary_currency: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
                <SelectItem value="GBP">GBP</SelectItem>
                <SelectItem value="INR">INR</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Min Salary</Label>
            <Input 
              type="number"
              value={formData.desired_salary_min} 
              onChange={(e) => setFormData({ ...formData, desired_salary_min: e.target.value })} 
            />
          </div>
          <div>
            <Label>Max Salary</Label>
            <Input 
              type="number"
              value={formData.desired_salary_max} 
              onChange={(e) => setFormData({ ...formData, desired_salary_max: e.target.value })} 
            />
          </div>
        </div>
      </Card>
    );
  }

  const companyInfo = getMostRecentCompanyInfo(candidate);

  return (
    <Card className="p-4">
      <div className="flex justify-between items-start mb-3">
        <span className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">Contact Info</span>
        <div className="flex gap-1">
          {(!candidate.email_primary || !candidate.phone_primary) && companyInfo.name && (
            <LushaDiscoverButton
              entityType="candidate"
              entityId={candidate.id}
              firstName={candidate.first_name || ''}
              lastName={candidate.last_name || ''}
              companyName={companyInfo.name}
              companyDomain={companyInfo.domain}
              linkedinUrl={candidate.linkedin_url}
              city={candidate.city}
              email={candidate.email_primary || undefined}
              onSuccess={() => onUpdate()}
              size="sm"
              variant="ghost"
            />
          )}
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
            <Pencil className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-3 text-sm">
        {candidate.email_primary && (
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-muted-foreground" />
            <span>{candidate.email_primary}</span>
          </div>
        )}
        {candidate.phone_primary && (
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-muted-foreground" />
            <span>{candidate.phone_primary}</span>
          </div>
        )}
        {candidate.city && (
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <span>{candidate.city}</span>
          </div>
        )}
        {candidate.employment_status && (
          <div className="flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-muted-foreground" />
            <span className="capitalize">{candidate.employment_status.replace(/_/g, ' ')}</span>
          </div>
        )}
        {formatSalary(candidate.desired_salary_min, candidate.desired_salary_max, candidate.desired_salary_currency) && (
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-muted-foreground" />
            <span>{formatSalary(candidate.desired_salary_min, candidate.desired_salary_max, candidate.desired_salary_currency)}</span>
          </div>
        )}
        {candidate.availability_date && (
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span>Available from {new Date(candidate.availability_date).toLocaleDateString()}</span>
          </div>
        )}
        {candidate.linkedin_url && (
          <a href={candidate.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-primary hover:underline">
            <Linkedin className="h-4 w-4" />
            <span>LinkedIn</span>
          </a>
        )}
        {candidate.github_url && (
          <a href={candidate.github_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-primary hover:underline">
            <Github className="h-4 w-4" />
            <span>GitHub</span>
          </a>
        )}
        {candidate.website_url && (
          <a href={candidate.website_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-primary hover:underline">
            <Globe className="h-4 w-4" />
            <span>Website</span>
          </a>
        )}
      </div>
    </Card>
  );
};

export default EditableContactInfo;
